import type { ContactUserFormik } from "../V3ContactUser";
import {
    V4PHAuthorMetaWrapper,
    V4PHContentWrapper,
    V4PHProfileAvatarImage,
    V4PHProfileAvatarWrapper,
    V4PHImageContainer,
    V4PHTopBar,
    MobileSocialsWrapper,
    V4PHContentMainWrapper,
    ProfileSocialsContainer,
    ImageAvatar,
    ModalMetadata,
    V3ProfileName,
    V4PHMainWrapper,
    AboutPageTitleWrapper,
    ProfileHeroSidebarHR,
    SibebarLayoutAboutPagesAlignment,
    UserThemableDisplayName,
    UserThemableBio,
    AboutPagesTitle
} from "./V4ProfileHero.styles";
import { getCoverInfo } from "../../utils/getCoverInfo";
import { useContext, useState } from "react";
import { ActionButtonType, ProfileView, UserCoverStyle, UserProfileStyle, UserResponse } from "@authory-packages/core";
import { V3Paragraph } from "../Typography";
import { EditableUIElement } from "../EditableUIElement";
import { V3IconUpload } from "../Icons";
import { SORTABLE_HOME_IDENTIFIER, getAuthoryColorScheme } from "./V4ProfileHero.utils";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3ProfileContact, V3ProfileSocials } from "./V3ProfileSocials";
import { AuthoryColorSchemes } from "./V3ProfileHero.types";
import { V3ProfileAvatar } from "../ProfileAvatar";
import { getAuthorName, getDisplayName } from "../../utils/getDisplayName";
import { isMainDomain } from "../../utils/domainRouting";
import { RequestHost } from "../../types/headers";
import { getDefaultAboutPageName } from "../../types/aboutPage";
import { DomainContext } from "../../context/domainContext";
import { UpdateProfileFormik } from "../V3EditProfilemodal";
import { SidebarLayoutShowDesktop, SidebarLayoutShowMobile } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { EditableSortableType } from "../EditableSortableItems/EditableSortableItems.types";
import { ReadOnlySortableItems } from "../EditableSortableItems/ReadOnlySortableItems";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { V3BlueRoundPlus } from "../ReusableStyledComponents/V3BlueRoundPlus";
import { V3AuthoryLogo } from "../AuthoryLogo";
import { OptionalNextLink } from "../ReusableStyledComponents/OptionalNextLink";
import { useWindowSize } from "../../hooks/useWindowSize";
import { AboutPagesMobileMenu } from "./AboutPagesMobileMenu";
import { CoverImagePreload } from "./CoverImagePreload";
import React from "react";
import { getAboutPagesWithhref } from "../../utils/getAboutPagesWithHref";
import { GenericContentEditable } from "../GenericContentEditable";
import { useTheme } from "styled-components";
import { CustomDarkShade } from "../ReusableStyledComponents/FontSetUtilities";
import { OptionalLink } from "../ReusableStyledComponents/OptionalLink";
import { getAuthorPageUrl } from "../../utils/getAuthorPageUrl";
import { ProfileUserResponse } from "@authory-packages/core";
import { ShareStatus } from "@authory-packages/core";
import dynamic from "next/dynamic";

const EditableSortableItems = dynamic(() => import("../EditableSortableItems/EditableSortableItems").then((m) => m.EditableSortableItems));
const EditableIt = dynamic(() => import("../EditableSortableItems/EditableSortableItems").then((m) => m.EditableIt));
const V3ContactUser = dynamic(() => import("../V3ContactUser/V3ContactUser").then((m) => m.V3ContactUser));
const V3SubscribePopup = dynamic(() => import("../SubscribePopup/V3SubscribePopup").then((m) => m.V3SubscribePopup));
const V3ModalBase = dynamic(() => import("../V3ModalBase/V3ModalBase").then((m) => m.V3ModalBase));

interface V4ProfileHeroProps {
    author: UserResponse | ProfileUserResponse;
    editMode?: boolean;
    initialContactOpen?: boolean;
    smartShareUrl?: string;
    aboutPageId?: string;
    aboutPageTitle?: string;
    isAboutPage: boolean;
    subscribeOpen?: boolean;
    onSocialsEditableClick?: () => void;
    onContactEditableClick?: () => void;
    onPIEditableClick?: () => void;
    onEditProfileModalSubmit?: (data: UpdateProfileFormik) => void;
    onAboutPageSubmitName?: (name: string, slug?: string) => Promise<any>;
    onAboutPageStopDrag?: (data: Record<string, number>) => void;
    onAboutPageDelete?: (id: string) => void;
    onAboutPageSelect: (id: string) => void;
    onAboutPageSubmitTitle?: (title: string, id: string) => Promise<any>;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    onSubscribeFormSubmit: (email: string) => Promise<void>;
    isProfileCollectionView?: boolean;
    isHeadlinePageHeading?: boolean;
}

export const V4ProfileHero = ({
    author,
    editMode = false,
    smartShareUrl,
    initialContactOpen = false,
    subscribeOpen = false,
    onSocialsEditableClick,
    onContactEditableClick,
    onContactUserSubmit,
    onPIEditableClick,
    onEditProfileModalSubmit,
    onAboutPageSubmitName,
    onAboutPageStopDrag,
    onAboutPageDelete,
    onAboutPageSelect,
    onAboutPageSubmitTitle,
    onSubscribeFormSubmit,
    isProfileCollectionView,
    aboutPageId,
    aboutPageTitle,
    isAboutPage,
    isHeadlinePageHeading = false
}: V4ProfileHeroProps) => {
    // Current Domain
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;
    const theme = useTheme();

    const [subscribeModalOpen, setSubscribeModalOpen] = useState(subscribeOpen);

    // Details modal
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);

    const size = useWindowSize();
    const isMobile = (size.width && size.width < theme.v3.layout.sidebarLayout.mobile) || false;

    // Contact modal
    const [contactModalOpen, setContactModalOpen] = useState(initialContactOpen);

    // Background and colors
    const { color, authorUploadedImage } = getCoverInfo(author);
    const coverDesktopSrc = !!author.coverUrl.length ? author.coverUrl[0].url : undefined;
    const coverMobileSrc = author.coverUrl.length > 1 ? author.coverUrl[1].url : undefined;
    const colorVariant = getAuthoryColorScheme(author.coverStyle as UserCoverStyle);

    // Profile type
    const isCenteredProfile = theme.customization.profileStyle === UserProfileStyle.centered;
    const isSidebar = theme.customization.profileStyle === UserProfileStyle.sidebar;

    const userHasProfileImage = !!author.avatarUrl.length;
    const userHasProfileComponents = !!author.profileComponents.length;
    const userHasHighlightButton = author.actionButtonType !== ActionButtonType.Disabled;
    const userHasSubscribe = author.displayEmailSubscribe;
    const hasImage = author.coverStyle === UserCoverStyle.image || author.coverStyle === UserCoverStyle.unsplash;

    const userHasComponents = userHasProfileComponents || userHasHighlightButton || userHasSubscribe;
    const userHasHighlightOrSubscribe = userHasHighlightButton || userHasSubscribe;

    const isColorNotWhite = author.coverStyle === UserCoverStyle.color && author.coverColor !== "#FFFFFF";
    const isColorWhite = author.coverStyle === UserCoverStyle.color && author.coverColor === "#FFFFFF";

    const userHasBio = !!author.bio.length;
    const userHasDisplayName = !!author.displayName.length;

    const enforceMinHeight = !userHasProfileImage && !userHasBio && !userHasDisplayName && !editMode && (authorUploadedImage || isColorNotWhite);
    const hasSomeContent = userHasProfileImage || userHasBio || userHasDisplayName;

    const onAboutPageTitleEnd = (value: string) => {
        onAboutPageSubmitTitle && aboutPageId && onAboutPageSubmitTitle(value, aboutPageId);
    };

    const onProfileDisplayNameEditEnd = (html: string) => {
        const data: UpdateProfileFormik = {
            displayName: html
        };

        onEditProfileModalSubmit && onEditProfileModalSubmit(data);
    };

    const onProfileBioEditEnd = (html: string) => {
        const data: UpdateProfileFormik = {
            bio: html
        };

        onEditProfileModalSubmit && onEditProfileModalSubmit(data);
    };

    const ProfileSocials = () => (
        <ProfileSocialsContainer
            $editMode={editMode}
            $desktopSideMargin={!userHasProfileComponents || !userHasHighlightOrSubscribe}
            $useDesktopAsColumn={theme.customization.profileStyle === UserProfileStyle.sidebar}
            $profileStyle={theme.customization.profileStyle}
        >
            <div>
                <EditableUIElement
                    editMode={editMode}
                    text="Add Social Media"
                    hasContent={userHasProfileComponents}
                    smallPadding={userHasProfileComponents}
                    colorVariant={colorVariant}
                    onClick={onSocialsEditableClick}
                    customBackgroundColor={color}
                >
                    <V3ProfileSocials colorVariant={colorVariant} author={author} customBackgroundColor={color} />
                </EditableUIElement>
            </div>
            {(editMode || userHasHighlightOrSubscribe) && (
                <div>
                    <EditableUIElement
                        editMode={editMode}
                        text="Add Contact & Subscribe"
                        hasContent={userHasHighlightOrSubscribe}
                        smallPadding={userHasHighlightOrSubscribe}
                        colorVariant={colorVariant}
                        onClick={onContactEditableClick}
                        customBackgroundColor={color}
                    >
                        <V3ProfileContact
                            colorVariant={colorVariant}
                            author={author}
                            openContactModal={() => setContactModalOpen(true)}
                            openSubscribeModal={() => setSubscribeModalOpen(true)}
                            customBackgroundColor={color}
                        />
                    </EditableUIElement>
                </div>
            )}
        </ProfileSocialsContainer>
    );

    const showHeadline = editMode || userHasDisplayName;

    const hasAboutPages = !!author.aboutPages.length;

    const pagesWithHref = getAboutPagesWithhref(requestHostHeader, editMode, author, smartShareUrl);

    const EditableSortableWrapperRenderer = editMode ? React.Fragment : SidebarLayoutShowDesktop;

    const UntitledPagesAmount = (author.aboutPages || []).length;

    const createNewPageHandler = () => onAboutPageSubmitName && onAboutPageSubmitName(getDefaultAboutPageName(UntitledPagesAmount + 1));

    const aboutPagesActiveCol = isProfileCollectionView ? undefined : aboutPageId || SORTABLE_HOME_IDENTIFIER;
    const aboutPagesScrollColor = author.coverStyle === UserCoverStyle.color ? author.coverColor : undefined;

    /**
     * Opens the picked about page. The home tab is a plain link, so it is skipped.
     * @param id The about page slug, or the home identifier
     */
    function onAboutPageItemSelect(id: string) {
        if (id === SORTABLE_HOME_IDENTIFIER) return;
        onAboutPageSelect(id);
    }

    const AboutPages = () =>
        !editMode ? (
            <ReadOnlySortableItems
                itemType={EditableSortableType.AboutPages}
                scrollCustomColor={aboutPagesScrollColor}
                colorVariant={colorVariant}
                its={pagesWithHref}
                activeCol={aboutPagesActiveCol}
                onItemSelect={onAboutPageItemSelect}
                customBackgroundColor={color}
                useVerticalMode={isSidebar && !isMobile}
            />
        ) : (
            <EditableSortableItems
                autoScroll={!isSidebar}
                itemType={EditableSortableType.AboutPages}
                scrollCustomColor={aboutPagesScrollColor}
                colorVariant={colorVariant}
                its={pagesWithHref}
                editMode
                activeCol={aboutPagesActiveCol}
                onItemSelect={onAboutPageItemSelect}
                customBackgroundColor={color}
                onCollectionStopDrag={onAboutPageStopDrag}
                onCollectionSubmitName={onAboutPageSubmitName}
                onCollectionRemoveFromProfile={onAboutPageDelete}
                renderShortcutAddItem={(_scrolling, _onOpenChange, simplebarScroll) => {
                    return (
                        <div
                            style={{ padding: 18, display: "flex" }}
                            onClick={() => {
                                createNewPageHandler();
                                simplebarScroll();
                            }}
                        >
                            <V3BlueRoundPlus />
                        </div>
                    );
                }}
                useVerticalMode={isSidebar && !isMobile}
                renderMainAddItem={(_scrolling, _onOpenChange, _simplebarScroll, setEditingComponentID, isEditing, itemsLength) => {
                    return (
                        <div
                            onClick={() => {
                                createNewPageHandler();
                            }}
                        >
                            <EditableIt
                                editMode
                                activeCol={""}
                                onSelectDrag={() => {}}
                                onItemSelect={() => {}}
                                onCollectionSubmitName={onAboutPageSubmitName}
                                setEditingComponentID={setEditingComponentID}
                                isEditing={isEditing}
                                totalItemsNumber={itemsLength}
                                addText={"Create page"}
                                itemType={EditableSortableType.AboutPages}
                                colorVariant={colorVariant}
                                customBackgroundColor={color}
                            />
                        </div>
                    );
                }}
            />
        );

    return (
        <>
            {authorUploadedImage && <CoverImagePreload desktopSrc={coverDesktopSrc} mobileSrc={coverMobileSrc} />}
            <V4PHImageContainer
                $useImage={authorUploadedImage}
                $coverColor={color}
                $desktopSrc={coverDesktopSrc}
                $mobileSrc={coverMobileSrc}
                $enforceMinHeight={enforceMinHeight}
                $isAboutPage={isAboutPage}
                $profileStyle={theme.customization.profileStyle}
                $isPrivateView={editMode}
            >
                <V4PHMainWrapper>
                    <V4PHContentWrapper>
                        {!editMode && hasAboutPages && (
                            <SidebarLayoutShowMobile>
                                <AboutPagesMobileMenu colorVariant={colorVariant} aboutPages={pagesWithHref} authorSlug={author.slug} />
                            </SidebarLayoutShowMobile>
                        )}
                        {(!isSidebar || isMobile) && (
                            <V4PHTopBar
                                $profileStyle={theme.customization.profileStyle}
                                $smallPadding={!hasSomeContent && !editMode}
                                $editMode={editMode}
                            >
                                <div>
                                    {!editMode && !hasAboutPages ? (
                                        author.profileAuthoryBranding ? (
                                            <SidebarLayoutShowDesktop>
                                                <OptionalNextLink
                                                    href={editMode ? undefined : `${APPLICATION_ROUTES.HOMEPAGE}?utm_source=portfolio&utm_medium=logo`}
                                                    target="_blank"
                                                >
                                                    <V3AuthoryLogo
                                                        color={colorVariant !== AuthoryColorSchemes.light ? theme.v3.colors.ink : "white"}
                                                        hideCopy={isMobile}
                                                        hasOpacity
                                                    />
                                                </OptionalNextLink>
                                            </SidebarLayoutShowDesktop>
                                        ) : null
                                    ) : (
                                        <EditableSortableWrapperRenderer>
                                            <AboutPages />
                                        </EditableSortableWrapperRenderer>
                                    )}
                                </div>
                                <div>
                                    <SidebarLayoutShowDesktop>
                                        <VerticalSpacing bottom={12}>
                                            <ProfileSocials />
                                        </VerticalSpacing>
                                    </SidebarLayoutShowDesktop>
                                </div>
                            </V4PHTopBar>
                        )}
                        {isAboutPage && !isSidebar ? (
                            <AboutPageTitleWrapper $colorVariant={colorVariant}>
                                <AboutPagesTitle $skipBaseColor $customBackgroundColor={color} $colorVariant={colorVariant}>
                                    <GenericContentEditable
                                        colorVariant={colorVariant}
                                        text={aboutPageTitle || ""}
                                        editMode={editMode}
                                        onEditCallback={onAboutPageTitleEnd}
                                        customBackgroundColor={color}
                                    />
                                </AboutPagesTitle>
                            </AboutPageTitleWrapper>
                        ) : (
                            <>
                                {(hasSomeContent || editMode) && (
                                    <V4PHContentMainWrapper
                                        $profileStyle={theme.customization.profileStyle}
                                        $viewType={theme.customization.defaultView as ProfileView}
                                        $isAboutPage={isAboutPage}
                                    >
                                        <V4PHAuthorMetaWrapper
                                            $colorVariant={colorVariant}
                                            $profileStyle={theme.customization.profileStyle}
                                            $editMode={editMode}
                                        >
                                            <div>
                                                {(editMode || userHasProfileImage) && (
                                                    <>
                                                        <EditableUIElement
                                                            colorVariant={colorVariant}
                                                            editMode={editMode}
                                                            smallPadding
                                                            customBorderRadius="50%"
                                                            hasContent={userHasProfileImage}
                                                            onClick={onPIEditableClick}
                                                            customNoContent={
                                                                <V4PHProfileAvatarWrapper
                                                                    $profileStyle={theme.customization.profileStyle}
                                                                    $isColorWhite={isColorWhite}
                                                                >
                                                                    <V3IconUpload
                                                                        stroke={hasImage ? theme.v3.colors.white : theme.v3.colors.grey70}
                                                                        width={24}
                                                                        height={24}
                                                                    />
                                                                </V4PHProfileAvatarWrapper>
                                                            }
                                                            customBackgroundColor={color}
                                                        >
                                                            {userHasProfileImage && (
                                                                <OptionalLink
                                                                    href={
                                                                        editMode
                                                                            ? undefined
                                                                            : getAuthorPageUrl(
                                                                                  author.profileShareStatus === ShareStatus.Published,
                                                                                  isMainDomain(requestHostHeader),
                                                                                  author.slug,
                                                                                  smartShareUrl
                                                                              )
                                                                    }
                                                                >
                                                                    <V4PHProfileAvatarWrapper
                                                                        $profileStyle={theme.customization.profileStyle}
                                                                        $isColorWhite={isColorWhite}
                                                                        $colorVariant={colorVariant}
                                                                        $customBackgroundColor={color}
                                                                    >
                                                                        <V4PHProfileAvatarImage
                                                                            src={userHasProfileImage ? author.avatarUrl[0].url : undefined}
                                                                            alt={getDisplayName(author)}
                                                                        />
                                                                    </V4PHProfileAvatarWrapper>
                                                                </OptionalLink>
                                                            )}
                                                        </EditableUIElement>
                                                    </>
                                                )}
                                            </div>
                                            <div>
                                                {showHeadline && (
                                                    <VerticalSpacing bottom={isSidebar ? 32 : 12}>
                                                        <UserThemableDisplayName
                                                            as={isHeadlinePageHeading ? "h1" : "div"}
                                                            $colorVariant={colorVariant}
                                                            $skipBaseColor
                                                            $customBackgroundColor={color}
                                                        >
                                                            <OptionalLink
                                                                href={
                                                                    editMode
                                                                        ? undefined
                                                                        : getAuthorPageUrl(
                                                                              author.profileShareStatus === ShareStatus.Published,
                                                                              isMainDomain(requestHostHeader),
                                                                              author.slug,
                                                                              smartShareUrl
                                                                          )
                                                                }
                                                            >
                                                                <GenericContentEditable
                                                                    colorVariant={colorVariant}
                                                                    text={author.displayName}
                                                                    editMode={editMode}
                                                                    onEditCallback={onProfileDisplayNameEditEnd}
                                                                    placeholder="Enter headline"
                                                                    customBackgroundColor={color}
                                                                />
                                                            </OptionalLink>
                                                        </UserThemableDisplayName>
                                                    </VerticalSpacing>
                                                )}
                                                {(editMode || userHasBio) && !isSidebar && (
                                                    <VerticalSpacing bottom={12}>
                                                        <UserThemableBio
                                                            as="div"
                                                            $colorVariant={colorVariant}
                                                            $skipBaseColor
                                                            $customBackgroundColor={color}
                                                            $profileStyle={author.profileStyle}
                                                        >
                                                            <GenericContentEditable
                                                                colorVariant={colorVariant}
                                                                text={author.bio}
                                                                editMode={editMode}
                                                                onEditCallback={onProfileBioEditEnd}
                                                                placeholder={"Enter subheadline"}
                                                                customBackgroundColor={color}
                                                            />
                                                        </UserThemableBio>
                                                    </VerticalSpacing>
                                                )}
                                            </div>
                                        </V4PHAuthorMetaWrapper>
                                        {isSidebar && (
                                            <>
                                                <SidebarLayoutShowDesktop>
                                                    {(editMode || hasAboutPages) && (
                                                        <SibebarLayoutAboutPagesAlignment
                                                            $editMode={editMode}
                                                            $profileStyle={theme.customization.profileStyle}
                                                        >
                                                            <AboutPages />
                                                        </SibebarLayoutAboutPagesAlignment>
                                                    )}
                                                    <VerticalSpacing top={24} bottom={48}>
                                                        <ProfileHeroSidebarHR
                                                            $colorVariant={colorVariant}
                                                            $customBackgroundColor={color}
                                                            $customDarkShade={CustomDarkShade.Grey70}
                                                        />
                                                    </VerticalSpacing>
                                                </SidebarLayoutShowDesktop>
                                            </>
                                        )}
                                        {(userHasComponents || editMode) && (
                                            <MobileSocialsWrapper $profileStyle={theme.customization.profileStyle}>
                                                <VerticalSpacing top={userHasProfileComponents || editMode ? (isSidebar && !isMobile ? 28 : 26) : 0}>
                                                    <ProfileSocials />
                                                </VerticalSpacing>
                                            </MobileSocialsWrapper>
                                        )}
                                    </V4PHContentMainWrapper>
                                )}
                            </>
                        )}
                    </V4PHContentWrapper>
                </V4PHMainWrapper>
            </V4PHImageContainer>
            {contactModalOpen && (
                <V3ContactUser
                    modelOpen={contactModalOpen}
                    onCloseHandler={() => setContactModalOpen(false)}
                    userName={getAuthorName(author)}
                    onContactUserSubmit={onContactUserSubmit}
                />
            )}
            {detailsModalOpen && (
                <V3ModalBase isOpen={detailsModalOpen} onClose={() => setDetailsModalOpen(false)}>
                    <ModalMetadata>
                        {!!author.avatarUrl.length && (
                            <VerticalSpacing bottom={isCenteredProfile ? 16 : 10}>
                                <ImageAvatar
                                    variant={UserProfileStyle.centered}
                                    background={!!author.avatarUrl.length ? author.avatarUrl[0].url : undefined}
                                >
                                    <V3ProfileAvatar
                                        authorName={getDisplayName(author)}
                                        size={"M"}
                                        src={!!author.avatarUrl.length ? author.avatarUrl[0].url : undefined}
                                    />
                                </ImageAvatar>
                            </VerticalSpacing>
                        )}
                        <VerticalSpacing bottom={24}>
                            <V3ProfileName color={AuthoryColorSchemes.userTheme}>{getDisplayName(author)}</V3ProfileName>
                        </VerticalSpacing>
                        {!!author.bio.length && (
                            <VerticalSpacing bottom={24}>
                                {author.bio.split("\n").map((line, index) => (
                                    <V3Paragraph key={index}>{line}</V3Paragraph>
                                ))}
                            </VerticalSpacing>
                        )}
                    </ModalMetadata>
                </V3ModalBase>
            )}
            {subscribeModalOpen && (
                <V3SubscribePopup
                    firstName={author.firstName}
                    name={`${author.firstName} ${author.lastName}`}
                    onSubscribeFormSubmit={onSubscribeFormSubmit}
                    hasPrivacyPolicy={false}
                    slug={author.slug}
                    disabled={!author.displayEmailSubscribe}
                    onCloseHandler={setSubscribeModalOpen}
                />
            )}
        </>
    );
};
